import moment from "moment";
import { NetworkRepEntry } from "../../../network/@types/Url";
import { Selection, Selections, Sort } from "../../@types";
import {
  extractNetworkValuesFromQuery,
  getSelectedPeriodTimerange
} from "../../util";
import { DATE_FILTER, FILTER_KEYWORDS, SEARCH_QUERY_LIMIT } from "../../consts";

export const filterByClassification = (
  item: NetworkRepEntry,
  classification: string
) => !classification || item.classification === classification;

export const filterByResourceType = (
  item: NetworkRepEntry,
  resourceType: string
) => !resourceType || item.type === resourceType;

export const PERIOD_TYPE_MAP: Record<string, keyof NetworkRepEntry> = {
  lastseen: "last_seen",
  firstseen: "first_seen"
};

export const filterByDate = (
  item: NetworkRepEntry,
  type: string,
  period?: Selection
) => {
  if (!period) return true;
  if (period?.value === "all") return true;
  const [start, end] = getSelectedPeriodTimerange(period);
  const date = item[PERIOD_TYPE_MAP[type]] as string;
  return moment(date).isBetween(start, end, "days", "[]");
};

export const formatNetworkSelections = (
  q?: string,
  selections?: Selections,
  isNetworkLocation?: boolean
): Selections => ({
  ...selections,
  period_type: !isNetworkLocation
    ? {
        value: DATE_FILTER.ALL,
        label: DATE_FILTER.ALL
      }
    : selections?.period_type,
  time_type:
    !!q && selections?.time_type?.value === FILTER_KEYWORDS.SUBMISSION_TIME
      ? {
          label: FILTER_KEYWORDS.FIRST_SEEN,
          value: FILTER_KEYWORDS.FIRST_SEEN
        }
      : selections?.time_type,
  sample_origin: {
    value: "url_analysis",
    label: "url_analysis"
  }
});

type NetworkRepSortFunction = (
  a: NetworkRepEntry,
  b: NetworkRepEntry
) => number;

const sortByThreatLevel: NetworkRepSortFunction = (
  a: NetworkRepEntry,
  b: NetworkRepEntry
) => {
  const aLevel = a.threat_level ?? -1;
  const bLevel = b.threat_level ?? -1;
  return aLevel - bLevel;
};

const sortByName =
  (type: "requested_network_location" | "type"): NetworkRepSortFunction =>
  (a: NetworkRepEntry, b: NetworkRepEntry) => {
    const aName = a[type] ?? "";
    const bName = b[type] ?? "";
    return aName.localeCompare(bName);
  };

const sortByReason = (a: NetworkRepEntry, b: NetworkRepEntry) => {
  const aReason = a.reason ?? "unknown";
  const bReason = b.reason ?? "unknown";
  return aReason.localeCompare(bReason);
};

const sortByDate =
  (type: "first_seen" | "last_seen"): NetworkRepSortFunction =>
  (a: NetworkRepEntry, b: NetworkRepEntry) => {
    const aDate = a[type] ?? "";
    const bDate = b[type] ?? "";
    return moment(aDate).diff(bDate);
  };

const sortByRepuation: NetworkRepSortFunction = (
  a: NetworkRepEntry,
  b: NetworkRepEntry
) => {
  const aRep =
    (a.third_party_reputations.malicious ?? 0) /
    (a.third_party_reputations.total || 1);
  const bRep =
    (b.third_party_reputations.malicious ?? 0) /
    (b.third_party_reputations.total || 1);
  return aRep - bRep;
};

const getSortFunction = (field: string): NetworkRepSortFunction | undefined => {
  switch (field) {
    case "threat_level":
      return sortByThreatLevel;
    case "resource":
      return sortByName("requested_network_location");
    case "type":
      return sortByName("type");
    case "first_seen":
      return sortByDate("first_seen");
    case "last_seen":
      return sortByDate("last_seen");
    case "reputation":
      return sortByRepuation;
    case "reason":
      return sortByReason;
    default:
      return undefined;
  }
};

export const sortNetworkReferences = (
  data: Array<NetworkRepEntry>,
  sort: Sort
) => {
  const { field, direction } = sort;

  const sortingFunction = getSortFunction(field);

  if (!sortingFunction) return data;

  if (direction === "desc") {
    return data.sort((a, b) => sortingFunction(b, a));
  }

  return data.sort(sortingFunction);
};

export const isNetworkOnlySearch = (q?: string, netRefs?: Array<string>) => {
  if (!q || q.length < SEARCH_QUERY_LIMIT) return false;
  if (netRefs?.length && q.length > SEARCH_QUERY_LIMIT) return true;

  return (
    netRefs === undefined &&
    q.length > SEARCH_QUERY_LIMIT &&
    extractNetworkValuesFromQuery(q).networkValues.length > 0
  );
};
